// src/store/slices/bootContextSlice.ts
// Boot context state for Central v2.3.0 compliance.
// Central switch-context response is the SOLE authority for workspace/app state.
//
// ARCHITECTURE: CRM is a CONSUMER only. Central is the Single Source of Truth.
//
// Types and slice factory come from @levelup/central-auth.
// This file creates the slice, exports actions, and defines app-scoped selectors.

import { createBootContextSlice } from '@levelup/central-auth';
import type { RootState } from '../store';

// Re-export all shared types for backward compatibility
export type {
  CapabilitiesMap,
  WorkspacePermissions,
  SwitchContextWorkspace,
  SuiteApp,
  SuiteAppExtended,
  CentralAvatar,
  AppTheme,
  AppIconAsset,
  AppUI,
  AppAssets,
  UIShellLayout,
  UIShellIconography,
  UIShell,
  SwitchContextMeta,
  SwitchContextUser,
  BootStatus,
  SwitchContextResponse,
  BootContextState,
} from '@levelup/central-auth';

// ============================================================
// Create slice from shared factory
// ============================================================

const { reducer, actions } = createBootContextSlice();

export default reducer;

// ============================================================
// Action Exports
// ============================================================

export const {
  setBootLoading,
  setBootReady,
  setBootWorkspaceClosed,
  setBootUnavailable,
  setBootUnauthenticated,
  resetBoot,
  updateBootContext,
  updateCurrentWorkspaceName,
  updateCurrentWorkspaceAvatar,
} = actions;

// ============================================================
// Selectors (app-specific — use CRM's RootState)
// ============================================================

export const selectBootStatus = (state: RootState) => state.bootContext.bootStatus;
export const selectBootApps = (state: RootState) => state.bootContext.apps;
export const selectBootOtherWorkspaces = (state: RootState) => state.bootContext.otherWorkspaces;
export const selectBootCurrentWorkspace = (state: RootState) => state.bootContext.currentWorkspace;
export const selectBootUser = (state: RootState) => state.bootContext.user;
export const selectBootMeta = (state: RootState) => state.bootContext.meta;
export const selectBootError = (state: RootState) => state.bootContext.error;
export const selectBootClosedWorkspaceResponse = (state: RootState) =>
  state.bootContext.closedWorkspaceResponse;
export const selectIsBootReady = (state: RootState) => state.bootContext.bootStatus === 'ready';

/**
 * Select the create workspace URL from Central's UI hints.
 */
export const selectCreateWorkspaceUrl = (state: RootState): string | null =>
  state.bootContext.meta?.uiHints?.createWorkspaceUrl ?? null;

/**
 * Select the current app ID for this application.
 */
export { APP_ID as selectCurrentAppId } from '../../config/centralAuth';
